import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import Plot from 'react-plotly.js';
import { REITCompareResponse, RatioDataPoint, DiffDataPoint } from '../types';
import { PriceMethod, Granularity } from './Dashboard';

interface AggregatedAnalysisProps {
  reitTickers: string[];
  selectedDays: number;
  priceMethod: PriceMethod;
  granularity: Granularity;
}

type SubTab = 'pb_diff' | 'price_ratio' | 'bv_ratio';

const CHART_COLOR = '#818cf8';

export default function AggregatedAnalysis({ reitTickers, selectedDays, priceMethod, granularity }: AggregatedAnalysisProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Use explicit colors like bonds dashboard
  const bgColor = isDarkMode ? '#1e293b' : '#ffffff';
  const gridColor = isDarkMode ? '#334155' : '#e2e8f0';
  const textColor = isDarkMode ? '#94a3b8' : '#64748b';
  // More contrasty spike color for better visibility
  const spikeColor = isDarkMode ? '#94a3b8' : '#475569';

  const [selectedTicker, setSelectedTicker] = useState<string>('');
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('pb_diff');
  const [chartData, setChartData] = useState<Map<string, REITCompareResponse>>(new Map());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Memoize other tickers - use JSON stringify to create stable key
  const otherTickersKey = useMemo(
    () => reitTickers.filter(t => t !== selectedTicker).join(','),
    [reitTickers, selectedTicker]
  );
  const otherTickers = useMemo(
    () => otherTickersKey.split(',').filter(Boolean),
    [otherTickersKey]
  );

  // Track last fetched params to prevent duplicate fetches
  const lastFetchRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch comparison data for selected ticker vs all others
  useEffect(() => {
    const fetchKey = `${selectedTicker}-${selectedDays}-${priceMethod}-${granularity}-${otherTickersKey}`;
    if (!selectedTicker || otherTickers.length === 0 || lastFetchRef.current === fetchKey) {
      return;
    }

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    lastFetchRef.current = fetchKey;
    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const results = await Promise.all(
          otherTickers.map(async (otherTicker) => {
            const res = await fetch(
              `/api/compare-reits?ticker1=${encodeURIComponent(selectedTicker)}&ticker2=${encodeURIComponent(otherTicker)}&days=${selectedDays}&method=${priceMethod}&granularity=${granularity}`,
              { signal }
            );
            if (!res.ok) throw new Error(`Failed to fetch ${selectedTicker} vs ${otherTicker}`);
            const data: REITCompareResponse = await res.json();
            return { ticker: otherTicker, data };
          })
        );

        // Only update state if not aborted
        if (!signal.aborted) {
          const newMap = new Map<string, REITCompareResponse>();
          results.forEach(({ ticker, data }) => {
            newMap.set(ticker, data);
          });
          setChartData(newMap);
        }
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        if (!signal.aborted) {
          setError(err instanceof Error ? err.message : 'Failed to fetch comparison data');
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [selectedTicker, selectedDays, priceMethod, granularity, otherTickers, otherTickersKey]);

  // Chart layout helper - memoized
  const chartLayout = useCallback((title: string, yAxisTitle: string, refLineY: number | null = null): any => ({
    title: {
      text: title,
      font: { size: 12, color: textColor },
    },
    paper_bgcolor: bgColor,
    plot_bgcolor: bgColor,
    font: { color: textColor, size: 10 },
    margin: { l: 40, r: 20, t: 30, b: 30 },
    xaxis: {
      showgrid: true,
      gridcolor: gridColor,
      gridwidth: 1,
      griddash: 'dot',
      linecolor: gridColor,
      zeroline: false,
      tickfont: { size: 9 },
      showspikes: true,
      spikemode: 'across+toaxis',
      spikesnap: 'cursor',
      spikethickness: 1.5,
      spikecolor: spikeColor,
      spikedash: 'dot',
      spikedistance: -1,
    },
    yaxis: {
      title: { text: yAxisTitle, font: { size: 10 } },
      showgrid: true,
      gridcolor: gridColor,
      gridwidth: 1,
      griddash: 'dot',
      linecolor: gridColor,
      zeroline: false,
      tickfont: { size: 9 },
      showspikes: true,
      spikemode: 'across+toaxis',
      spikesnap: 'cursor',
      spikethickness: 1.5,
      spikecolor: spikeColor,
      spikedash: 'dot',
      spikedistance: -1,
    },
    showlegend: false,
    hovermode: 'closest',
    shapes: refLineY !== null ? [{
      type: 'line',
      x0: 0,
      x1: 1,
      xref: 'paper',
      y0: refLineY,
      y1: refLineY,
      line: { color: gridColor, width: 1, dash: 'dot' },
    }] : [],
  }), [bgColor, gridColor, textColor, spikeColor]);

  const chartConfig: any = useMemo(() => ({
    responsive: true,
    displayModeBar: false,
  }), []);

  // Filter helpers - memoized
  const filterRatio = useCallback((arr: RatioDataPoint[]) =>
    arr.filter((d) => d.ratio != null).map((d) => ({ x: d.date, y: d.ratio })), []);

  const filterDiff = useCallback((arr: DiffDataPoint[]) =>
    arr.filter((d) => d.diff != null).map((d) => ({ x: d.date, y: d.diff })), []);

  // Render a single small chart - memoized
  const renderChart = useCallback((otherTicker: string) => {
    const data = chartData.get(otherTicker);
    if (!data) return null;

    let plotData: any[];
    let layout: any;

    switch (activeSubTab) {
      case 'pb_diff':
        plotData = [{
          x: filterDiff(data.priceBookDiff).map(d => d.x),
          y: filterDiff(data.priceBookDiff).map(d => d.y),
          type: 'scatter',
          mode: 'lines',
          line: { color: CHART_COLOR, width: 1.5 },
          fill: 'tozeroy',
          fillcolor: 'rgba(129, 140, 248, 0.2)',
          hovertemplate: '%{x|%b %d, %Y %H:%M}<br>%{y:.3f}<extra></extra>',
        }];
        layout = chartLayout(`vs ${otherTicker}`, 'P/B Diff', 0);
        break;

      case 'price_ratio':
        plotData = [{
          x: filterRatio(data.priceRatio).map(d => d.x),
          y: filterRatio(data.priceRatio).map(d => d.y),
          type: 'scatter',
          mode: 'lines',
          line: { color: CHART_COLOR, width: 1.5 },
          hovertemplate: '%{x|%b %d, %Y %H:%M}<br>%{y:.3f}x<extra></extra>',
        }];
        layout = chartLayout(`vs ${otherTicker}`, 'Price Ratio', 1);
        break;

      case 'bv_ratio':
        plotData = [{
          x: filterRatio(data.bookValueRatio).map(d => d.x),
          y: filterRatio(data.bookValueRatio).map(d => d.y),
          type: 'scatter',
          mode: 'lines',
          line: { color: CHART_COLOR, width: 1.5 },
          hovertemplate: '%{x|%b %d, %Y %H:%M}<br>%{y:.3f}x<extra></extra>',
        }];
        layout = chartLayout(`vs ${otherTicker}`, 'BV Ratio', 1);
        break;
    }

    return (
      <Grid item xs={6} sm={4} md={3} key={otherTicker}>
        <Paper sx={{ p: 1, height: 200 }}>
          <Plot
            data={plotData}
            layout={layout}
            config={chartConfig}
            style={{ width: '100%', height: '100%' }}
          />
        </Paper>
      </Grid>
    );
  }, [chartData, activeSubTab, chartLayout, chartConfig, filterDiff, filterRatio]);

  return (
    <Box>
      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Ticker</InputLabel>
              <Select
                value={selectedTicker}
                label="Select Ticker"
                onChange={(e) => setSelectedTicker(e.target.value)}
              >
                {reitTickers.map((ticker) => (
                  <MenuItem key={ticker} value={ticker}>
                    {ticker}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Tabs
              value={activeSubTab}
              onChange={(_, value) => setActiveSubTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="P/B Difference" value="pb_diff" />
              <Tab label="Price Ratio" value="price_ratio" />
              <Tab label="Book Value Ratio" value="bv_ratio" />
            </Tabs>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Charts Grid */}
      {!loading && selectedTicker && (
        <>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedTicker} vs All Other Tickers
          </Typography>
          <Grid container spacing={2}>
            {otherTickers.map(renderChart)}
          </Grid>
        </>
      )}

      {/* Placeholder */}
      {!selectedTicker && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Select a ticker to compare against all other REITs
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
